import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import CTABanner from "../components/CTABanner";
import PageMeta from "../components/PageMeta";
import { getGeoLocation, submitLead, redirectToApplication } from "../services/api";
import { COUNTRIES, findCountryByCode, DEFAULT_COUNTRY } from "../data/countries";

const WIKI_TYPES = [
  { value: "individual", label: "Individual Page" },
  { value: "company", label: "Company Page" },
];

export default function Contact() {
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    countryCode: DEFAULT_COUNTRY?.code || "US",
    wikiType: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const country = useMemo(
    () => findCountryByCode(values.countryCode) || DEFAULT_COUNTRY,
    [values.countryCode]
  );

  useEffect(() => {
    let active = true;
    getGeoLocation().then((g) => {
      if (!active) return;
      const match = findCountryByCode(g?.data?.countryCode);
      if (match) setValues((v) => ({ ...v, countryCode: match.code }));
    });
    return () => {
      active = false;
    };
  }, []);

  const set = (k) => (e) => {
    const val = typeof e === "string" ? e : e?.target?.value ?? "";
    setValues((p) => ({ ...p, [k]: val }));
  };
  const onPhone = (e) =>
    setValues((p) => ({
      ...p,
      phone: (e.target.value || "").replace(/\D+/g, "").slice(0, 10),
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!values.name.trim()) return setError("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      return setError("Please enter a valid email.");
    if (!values.phone || values.phone.length !== 10)
      return setError("Please enter a valid 10-digit phone number.");
    if (!values.wikiType)
      return setError("Please select the type of Wikipedia page you need.");
    if (!values.message.trim())
      return setError("Please share a short message about your project.");

    setSubmitting(true);
    const r = await submitLead({
      name: values.name,
      email: values.email,
      countryDialCode: country.dial,
      country: country.code,
      phoneNumber: values.phone,
      description:
        values.message ||
        `Wiki Type: ${WIKI_TYPES.find((t) => t.value === values.wikiType)?.label || "General enquiry"}`,
    });
    setSubmitting(false);
    if (r.ok) {
      setSuccess("Success! Redirecting...");
      setTimeout(() => redirectToApplication(r.customerId), 600);
      return;
    }
    setError(r.message || "Submission failed. Please try again.");
  };

  return (
    <div className="overflow-hidden" data-testid="contact-page">
      <PageMeta
        title="Contact Prime Wiki Services — Talk to a Wikipedia Specialist"
        description="Get a free 20-minute consultation with a senior Wikipedia editor. Confidential, no-pressure, NDA-protected. We respond to every inquiry within two hours on business days."
        keywords="contact wikipedia agency, wikipedia consultation, free notability review, talk to wikipedia editor, wikipedia specialist contact"
      />

      {/* Hero / banner — consistent with the rest of the site */}
      <section className="mwb-hero-bg px-4 py-16 sm:px-6 lg:px-8" data-testid="contact-hero">
        <div className="mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
            <Sparkles className="h-3.5 w-3.5" /> Contact Us
          </span>
          <h1 className="font-serif mt-5 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">
            Talk to a senior Wikipedia specialist.
          </h1>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">
            Book a free 20-minute consult or send us the details below. Every message is reviewed by an editor — not a bot — and replied to within two hours on business days.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-5 text-sm text-slate-600">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> NDA-protected engagement</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Free notability review</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Replies in under 2 hours</span>
          </div>
        </div>
      </section>

      {/* Contact form (only remaining content section) */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <form
          onSubmit={onSubmit}
          className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_30px_60px_-30px_rgba(10,37,64,0.25)] sm:p-10"
          data-testid="contact-form"
        >
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">
            <ShieldCheck className="h-4 w-4" /> Confidential &amp; NDA-protected
          </div>
          <h2 className="font-serif mt-3 text-3xl font-semibold leading-tight text-[#0A2540] sm:text-4xl">
            Send us a message
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Your details stay private. A specialist will respond within two hours on business days.
          </p>

          <div className="mt-7 grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="c-name">Name <span className="text-rose-500">*</span></Label>
              <Input
                id="c-name"
                value={values.name}
                onChange={set("name")}
                disabled={submitting}
                required
                className="mt-1 h-12"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <Label htmlFor="c-email">Email <span className="text-rose-500">*</span></Label>
              <Input
                id="c-email"
                type="email"
                value={values.email}
                onChange={set("email")}
                disabled={submitting}
                required
                className="mt-1 h-12"
                data-testid="contact-email-input"
              />
            </div>
          </div>

          <div className="mt-4">
            <Label>Phone <span className="text-rose-500">*</span></Label>
            <div className="mt-1 flex gap-2">
              <Select
                value={values.countryCode}
                onValueChange={set("countryCode")}
                disabled={submitting}
              >
                <SelectTrigger className="h-12 w-[130px] shrink-0" data-testid="contact-country-select">
                  <SelectValue>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-lg">{country?.flag}</span>
                      <span className="text-sm">+{country?.dial}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="inline-flex items-center gap-2">
                        <span>{c.flag}</span>
                        <span className="text-sm">{c.name}</span>
                        <span className="text-xs text-slate-500">+{c.dial}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                inputMode="numeric"
                placeholder="10-digit phone number"
                value={values.phone}
                onChange={onPhone}
                disabled={submitting}
                required
                maxLength={10}
                pattern="\d{10}"
                className="h-12"
                data-testid="contact-phone-input"
              />
            </div>
          </div>

          <div className="mt-4">
            <Label>Wiki Page Type <span className="text-rose-500">*</span></Label>
            <Select value={values.wikiType} onValueChange={set("wikiType")} disabled={submitting} required>
              <SelectTrigger className="mt-1 h-12" data-testid="contact-wikitype-select">
                <SelectValue placeholder="Choose a page type" />
              </SelectTrigger>
              <SelectContent>
                {WIKI_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4">
            <Label htmlFor="c-msg">Message <span className="text-rose-500">*</span></Label>
            <Textarea
              id="c-msg"
              rows={5}
              value={values.message}
              onChange={set("message")}
              disabled={submitting}
              required
              className="mt-1"
              data-testid="contact-message-input"
            />
          </div>

          {error ? (
            <div
              className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="contact-error"
            >
              <AlertCircle className="mt-0.5 h-4 w-4" />
              {error}
            </div>
          ) : null}
          {success ? (
            <div
              className="mt-4 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
              data-testid="contact-success"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4" />
              {success}
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={submitting}
            className="mt-7 h-12 w-full rounded-full text-sm font-semibold text-white"
            style={{ background: "linear-gradient(135deg,#0A2540 0%,#10B981 160%)" }}
            data-testid="contact-submit-button"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
              </span>
            ) : (
              "Send Message"
            )}
          </Button>
        </form>
      </section>

      {/* Final CTA banner — same component as the homepage */}
      <CTABanner />
    </div>
  );
}
